import { Component, OnInit, Input, SimpleChanges, ViewChild, ElementRef, forwardRef, Renderer2 } from '@angular/core';
import { IonIcon, IonSpinner, ToastController } from '@ionic/angular/standalone';
import { ReactiveFormsModule, ControlContainer, FormGroupDirective, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { DemoozInputComponent } from '@demooz/components';
import { DemoozGoogleMapsService } from '@demooz/services';
import { environment } from '@app/environment';

export class DemoozInputAddress {
    
    streetNumber: string;
    route: string;
    city: string;
    country: string;
    postalCode: string;
    latitude: number;
    longitude: number;
    fullAddress: string;
    
    constructor(streetNumber: string, route: string, city: string, country: string, postalCode: string, latitude: number, longitude: number, fullAddress: string) {
        this.streetNumber = streetNumber;
        this.route = route;
        this.city = city;
        this.country = country;
        this.postalCode = postalCode;
        this.latitude = latitude;
        this.longitude = longitude;
        this.fullAddress = fullAddress;
    }
    
    isValid(): boolean {
        return (this.route !== undefined && this.route.length > 0)
            && (this.city !== undefined && this.city.length > 0)
            && (this.country !== undefined && this.country.length > 0)
            && (this.postalCode !== undefined && this.postalCode.length > 0)
            && (this.latitude !== undefined && this.latitude.toString().length > 0)
            && (this.longitude !== undefined && this.longitude.toString().length > 0);
    }
}

class GooglePlace { 
    id: string;
    name: string;
}

@Component({
    selector: 'demooz-input-address',
    templateUrl: './input-address.component.html',
    styleUrls: ['./input-address.component.scss'],
    imports: [IonIcon, IonSpinner, TranslatePipe, ReactiveFormsModule, forwardRef(() => DemoozInputComponent)],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ]
})
export class DemoozInputAddressComponent implements OnInit {

    @Input({alias : 'label', required : false}) labelInput: string = '';
    @Input({alias : 'fixedLabel', required : false}) fixedLabel: boolean = false;
    @Input({alias : 'icon', required : false}) iconInput: string = '';
    @Input({alias : 'formName', required : true}) formName: string|number = '';
    @Input({alias : 'errorText', required : false}) errorText: string = this.translateService.instant('MAIN.MSG_ERROR_FIELD_MANDATORY');
    @Input({alias : 'disabled', required : false}) disabled: boolean = false;
    @Input({alias : 'autocomplete', required : false}) autocomplete: boolean = false;
    @Input({alias : 'showBtnClearInput', required : false}) showBtnClearInput: boolean = false;
    @Input({alias : 'noMargin', required : false}) noMargin: boolean = false;
    @Input({alias : 'textAlign', required : false}) textAlign: 'left'|'center'|'right' = 'left';
    @Input({alias : 'countries', required : false}) countries: Array<string> = ['FR'];
    
    @ViewChild('demoozInputAddress') demoozInputAddress: ElementRef;
    @ViewChild('demoozInput') demoozInput: DemoozInputComponent;
   
    isFetchingLocation: boolean = false;
    isFocusInput: boolean = false;
    isSearching: boolean = false;
    isFetchingSearch: boolean = false;
    onValueChange: Subscription; 
    valueChangeTimeout: any;   
    address: DemoozInputAddress|null = null;
    addressResults: Array<GooglePlace> = [];
    minCharactersSearch: number = 3;
    maxAddressResults: number = 3;
    sessionToken: any;

    constructor(
        private renderer: Renderer2,
        public translateService: TranslateService,
        private toastController: ToastController,
        public formGroupDirective: FormGroupDirective,
        private demoozGoogleMapsService: DemoozGoogleMapsService
    ) {
       
        this.renderer.listen('window', 'click',(e:Event)=> {
            if (!this.demoozInputAddress.nativeElement.contains(e.target)) {
                this.hideAddressResults();
            }            
        }); 
        
    }

    async ngOnInit() {     
            
        let AutocompleteSessionToken = await this.demoozGoogleMapsService.AutocompleteSessionToken(); 
        this.sessionToken = new AutocompleteSessionToken();
               
        this.onValueChange = this.formGroupDirective.form.controls[this.formName].valueChanges.subscribe((s) => {
            
            this.isFocusInput = true;
            
            if(this.valueChangeTimeout){
                clearTimeout(this.valueChangeTimeout);
            }
            
            this.valueChangeTimeout = setTimeout(() => {                
                this.getAddressFromSearch();
            }, 1000);
            
            if(this.formGroupDirective.form.controls[this.formName].value.toString().length === 0){
                this.address = null;
            }
        });        
    }
    
    async ngOnChanges(changes: SimpleChanges) {                
        if(changes['disabled'] && changes['disabled'].currentValue !== null){    
            if(this.disabled === true){
                this.formGroupDirective.form.controls[this.formName].disable({emitEvent: false});
            }
            else{
                this.formGroupDirective.form.controls[this.formName].enable({emitEvent: false});
            }
        }
    }
    
    ngOnDestroy() {           
        if(this.onValueChange){
            this.onValueChange.unsubscribe();
        }
        if(this.valueChangeTimeout){
            clearTimeout(this.valueChangeTimeout);
        }
    }
    
    onFocusInput($event: any){
        this.isFocusInput = true;
    }
    
    hideAddressResults(){
        this.isFocusInput = false;
        this.isSearching = false;
        this.addressResults = [];
    }
    
    async locateMe() {        
        if(!this.isFetchingLocation){
            this.isFetchingLocation = true;
            let address = await this.getCurrentPosition();
            this.isFetchingLocation = false;      
            if(address){
                this.setAddress(address);
            }      
        }
    }
    
    async getCurrentPosition(): Promise<DemoozInputAddress|null>{
        return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                async (position) => {                    
                    let address = await this.getAddressFromGeolocation(position.coords.latitude, position.coords.longitude);
                    resolve(address);
                },
                async (err) => { 
                    const toast = await this.toastController.create({
                        message: this.translateService.instant('INPUT_ADDRESS.MSG_ERROR_LOCATE_ME'),
                        duration: 6000,
                        position: 'top',
                        color: 'danger'
                    });
                    await toast.present();
                    resolve(null);
                },
                {enableHighAccuracy: true},
            );
        });
    }
    
    async initAddress(){
        let address: DemoozInputAddress|null = null;
        let search = this.formGroupDirective.form.controls[this.formName].value?.toString();
        if(search){
            let Geocoder = await this.demoozGoogleMapsService.Geocoding();        
            let response = await (new Geocoder()).geocode({address: search});   
            if(response.results[0] && response.results[0].address_components){
                let addressComponents = response.results[0]?.address_components;
                
                let streetNumber = this.findAddressElement(addressComponents, 'street_number');
                let route = this.findAddressElement(addressComponents, 'route');
                let city = this.findAddressElement(addressComponents, 'locality');
                let country = this.findAddressElement(addressComponents, 'country');
                let postalCode = this.findAddressElement(addressComponents, 'postal_code');

                let fullAddress = route + ', ' + postalCode + ' ' + city + ', ' + country;   
                let responseGPS = await (new Geocoder()).geocode({address: fullAddress});              
                
                address = new DemoozInputAddress(
                    streetNumber,
                    route,
                    city,
                    country,
                    postalCode,
                    responseGPS?.results[0]?.geometry?.location?.lat() || response.results[0].geometry.location.lat(),
                    responseGPS?.results[0]?.geometry?.location?.lng() || response.results[0].geometry.location.lng(),
                    response.results[0].formatted_address
                );  
            }
        }
        this.address = address;
    }
    
    async getAddressFromGeolocation(latitude: number, longitude: number): Promise<DemoozInputAddress|null> {
        let address: DemoozInputAddress|null = null;
        let Geocoder = await this.demoozGoogleMapsService.Geocoding();        
        let response = await (new Geocoder()).geocode({location: {lat: latitude, lng: longitude}});        
        if(response.results[0] && response.results[0].address_components){
            let addressComponents = response.results[0]?.address_components;
            
            let streetNumber = this.findAddressElement(addressComponents, 'street_number');
            let route = this.findAddressElement(addressComponents, 'route');
            let city = this.findAddressElement(addressComponents, 'locality');
            let country = this.findAddressElement(addressComponents, 'country');
            let postalCode = this.findAddressElement(addressComponents, 'postal_code');
            
            let fullAddress = route + ', ' + postalCode + ' ' + city + ', ' + country;   
            let responseGPS = await (new Geocoder()).geocode({address: fullAddress});  
            
            address = new DemoozInputAddress(
                streetNumber,
                route,
                city,
                country,
                postalCode,
                responseGPS?.results[0]?.geometry?.location?.lat() || response.results[0].geometry.location.lat(),
                responseGPS?.results[0]?.geometry?.location?.lng() || response.results[0].geometry.location.lng(),
                response.results[0].formatted_address
            );            
        }
        else{
            const toast = await this.toastController.create({
                message: this.translateService.instant('INPUT_ADDRESS.MSG_ERROR_FIND_ADDRESS'),
                duration: 6000,
                position: 'top',
                color: 'danger'
            });
            await toast.present();
        }
        return address;
    }
    
    async getAddressFromSearch(){
        let addressResults: Array<GooglePlace> = [];
        let search = this.formGroupDirective.form.controls[this.formName].value?.toString();
        if(search.length >= this.minCharactersSearch){     
            this.isFetchingSearch = true;          
            let AutocompleteSuggestion = await this.demoozGoogleMapsService.AutocompleteSuggestion(); 
            let response = await AutocompleteSuggestion.fetchAutocompleteSuggestions({input: search, includedRegionCodes: this.countries, sessionToken: this.sessionToken});             
            if(response?.suggestions?.length > 0){
                let results = response.suggestions.splice(0, this.maxAddressResults);
                for(let r of results){
                    if(r.placePrediction){                      
                        addressResults.push({
                            id: r.placePrediction.placeId,
                            name: r.placePrediction.text.text
                        });
                    }
                }
            }     
            this.isFetchingSearch = false;   
        }  
        this.isSearching = true;           
        this.addressResults = addressResults;
    }
    
    findAddressElement (addressArray: Array<any>, element: string): string {
        let i = addressArray.find(a => a.types.includes(element));
        return (i?.longText || i?.long_name || '');
    };
    
    async selectAddress(googlePlace: GooglePlace){       
        let address: DemoozInputAddress|null = null;
        let Place = await this.demoozGoogleMapsService.Place(); 
        let {place} = await (new Place({id: googlePlace.id})).fetchFields({fields:  ['addressComponents', 'formattedAddress', 'location']});                        
        if(place.addressComponents && place.formattedAddress && place.location){
            
            let streetNumber = this.findAddressElement(place.addressComponents, 'street_number');
            let route = this.findAddressElement(place.addressComponents, 'route');
            let city = this.findAddressElement(place.addressComponents, 'locality');
            let country = this.findAddressElement(place.addressComponents, 'country');
            let postalCode = this.findAddressElement(place.addressComponents, 'postal_code');
            
            let Geocoder = await this.demoozGoogleMapsService.Geocoding();  
            let fullAddress = route + ', ' + postalCode + ' ' + city + ', ' + country;   
            let responseGPS = await (new Geocoder()).geocode({address: fullAddress});  
          
            address = new DemoozInputAddress(
                streetNumber,
                route,
                city,
                country,
                postalCode,
                responseGPS?.results[0]?.geometry?.location?.lat() || place.location.lat(),
                responseGPS?.results[0]?.geometry?.location?.lng() || place.location.lng(),
                place.formattedAddress
            );
        }
        this.setAddress(address);
    }
    
    setAddress(address: DemoozInputAddress|null, init: boolean = false){
        if(!init){
            if(address && !address.isValid()){
                address = null;
            }      
        }  
        this.formGroupDirective.form.controls[this.formName].setValue((address ? address.fullAddress : null), {emitEvent: false});
        this.address = address;
        this.hideAddressResults();
    }
    
    getAddress(){
        return this.address;
    }
}
